import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { Calendar, Clock, MapPin, Video, MessageSquare, Users, RefreshCw, XCircle, Loader2, CalendarClock } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { MessageThread } from '@/components/MessageThread';
import { getMediationAreaLabel } from '@/lib/mediationAreaMapping';
import { UnreadBadge } from '@/components/UnreadBadge';
import { PaymentProgress } from '@/components/PaymentProgress';
import { DownloadReceiptButton } from '@/components/DownloadReceiptButton';
import {
  isWithinCancellationWindow,
  useCancelConsultationViaEdge,
} from '@/hooks/useCancelConsultation';
import { toast } from '@/hooks/use-toast';

interface ConsultationCardProps {
  consultation: {
    id: string;
    mediation_area: string;
    scheduled_date?: string | null;
    scheduled_time?: string | null;
    meeting_type?: string | null;
    location?: string | null;
    status?: string | null;
    seeker_paid?: boolean | null;
    other_party_paid?: boolean | null;
    other_party_name?: string | null;
    zoom_meeting_url?: string | null;
    seeker_message?: string | null;
    problem_description?: string | null;
    meeting_number?: number | null;
    amount_paid_cents?: number | null;
    duration_min?: number | null;
    seeker?: { full_name?: string; email?: string; phone?: string } | null;
    mediator?: { full_name?: string; email?: string } | null;
  };
  userType: 'mediator' | 'seeker';
  unreadCount?: number;
  onDetailsClick?: () => void;
  onScheduleFollowUp?: () => void;
  showPaymentStatus?: boolean;
  highlighted?: boolean;
}

export function ConsultationCard({
  consultation,
  userType,
  unreadCount = 0,
  onDetailsClick,
  onScheduleFollowUp,
  showPaymentStatus = true,
  highlighted = false,
}: ConsultationCardProps) {
  const otherPartyName = userType === 'mediator' 
    ? consultation.seeker?.full_name || 'Client'
    : consultation.mediator?.full_name || 'Médiateur';

  const navigate = useNavigate();
  const cancelMutation = useCancelConsultationViaEdge();
  const [cancelReason, setCancelReason] = useState('');
  const [cancelOpen, setCancelOpen] = useState(false);
  const [rescheduleOpen, setRescheduleOpen] = useState(false);

  const status = consultation.status || '';
  const isTerminal = status === 'cancelled' || status === 'completed';
  // Cancel/reschedule are gated client-side by the same 24h rule the DB
  // trigger enforces, so users see a disabled button + explanation rather
  // than a server error after clicking.
  const within24h = !isWithinCancellationWindow(
    consultation.scheduled_date,
    consultation.scheduled_time,
  );
  const isCancellable = !isTerminal && !within24h;
  // Mediators can also cancel; reschedule (= cancel + rebook) is offered to
  // the seeker only since they own the booking flow on /trouver-un-mediateur.
  const canReschedule = isCancellable && userType === 'seeker';

  const runCancel = async (afterCancel?: () => void) => {
    try {
      const result = await cancelMutation.mutateAsync({
        consultationId: consultation.id,
        reason: cancelReason.trim() || undefined,
      });
      const refundCount = result.refunds.filter((r) => r.refund_id).length;
      toast({
        title: 'Consultation annulée',
        description:
          refundCount > 0
            ? `Les créneaux ont été libérés. ${refundCount} remboursement(s) Stripe en cours.`
            : 'Les créneaux réservés ont été libérés.',
      });
      setCancelReason('');
      setCancelOpen(false);
      setRescheduleOpen(false);
      afterCancel?.();
    } catch (error) {
      const code = (error as Error & { code?: string }).code;
      const message =
        code === 'cancellation_window_closed'
          ? "Annulation impossible à moins de 24 h du rendez-vous."
          : (error as Error).message || "Impossible d'annuler la consultation.";
      toast({ title: 'Erreur', description: message, variant: 'destructive' });
    }
  };

  const handleConfirmReschedule = () =>
    runCancel(() => navigate('/trouver-un-mediateur'));

  const getStatusBadge = () => {
    const status = consultation.status;
    if (status === 'payment_completed' || status === 'confirmed' || status === 'paid') {
      return <Badge variant="default" className="bg-green-100 text-green-800 hover:bg-green-100">Confirmé</Badge>;
    }
    if (status === 'pending') {
      return <Badge variant="secondary" className="bg-yellow-100 text-yellow-800 hover:bg-yellow-100">En attente</Badge>;
    }
    if (status === 'pending_other_party') {
      return <Badge variant="secondary" className="bg-yellow-100 text-yellow-800 hover:bg-yellow-100">En attente (autre partie)</Badge>;
    }
    if (status === 'pending_payment_seeker') {
      return <Badge variant="secondary" className="bg-orange-100 text-orange-800 hover:bg-orange-100">Paiement requis (demandeur)</Badge>;
    }
    if (status === 'pending_payment_other_party') {
      return <Badge variant="secondary" className="bg-orange-100 text-orange-800 hover:bg-orange-100">Paiement requis (autre partie)</Badge>;
    }
    if (status === 'completed') {
      return <Badge variant="outline">Terminé</Badge>;
    }
    if (status === 'cancelled') {
      return <Badge variant="destructive">Annulé</Badge>;
    }
    return <Badge variant="outline">{status}</Badge>;
  };

  return (
    <Card className={`border transition-shadow hover:shadow-md ${highlighted ? 'border-primary/30 bg-primary/5' : ''}`}>
      <CardContent className="p-4">
        <div className="flex flex-col gap-4">
          {/* Header with name and status */}
          <div className="flex items-start justify-between gap-2">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary/10 rounded-full flex-shrink-0">
                <Users className="h-4 w-4 text-primary" />
              </div>
              <div>
                <h3 className="font-semibold text-foreground">
                  {otherPartyName}
                  {consultation.meeting_number && consultation.meeting_number > 1 && (
                    <span className="ml-2 text-xs font-normal text-muted-foreground">
                      Séance n°{consultation.meeting_number}
                    </span>
                  )}
                </h3>
                <p className="text-sm text-muted-foreground">
                  {getMediationAreaLabel(consultation.mediation_area)}
                  {consultation.meeting_number === 1 && (
                    <span className="ml-1">· Séance n°1</span>
                  )}
                </p>
              </div>
            </div>
            {getStatusBadge()}
          </div>

          {/* Date, time, and meeting type */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
            <div className="flex items-center gap-2 text-muted-foreground">
              <Calendar className="h-4 w-4 flex-shrink-0" />
              {consultation.scheduled_date ? (
                <span>{format(new Date(consultation.scheduled_date), "EEEE d MMMM yyyy", { locale: fr })}</span>
              ) : (
                <span className="italic">Date à définir</span>
              )}
            </div>
            
            {consultation.scheduled_time && (
              <div className="flex items-center gap-2 text-muted-foreground">
                <Clock className="h-4 w-4 flex-shrink-0" />
                <span>{consultation.scheduled_time.slice(0, 5)}</span>
              </div>
            )}
            
            <div className="flex items-center gap-2 text-muted-foreground">
              {consultation.meeting_type === 'video' ? (
                <>
                  <Video className="h-4 w-4 flex-shrink-0" />
                  <span>Visioconférence</span>
                </>
              ) : (
                <>
                  <MapPin className="h-4 w-4 flex-shrink-0" />
                  <span>En personne{consultation.location && ` - ${consultation.location}`}</span>
                </>
            )}
          </div>

          {/* Zoom link */}
          {consultation.zoom_meeting_url && (
            <div className="pt-1">
              <a
                href={consultation.zoom_meeting_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-sm text-primary hover:underline font-medium"
              >
                <Video className="h-4 w-4" />
                Rejoindre la visioconférence
              </a>
            </div>
          )}

            {consultation.other_party_name && (
              <div className="flex items-center gap-2 text-muted-foreground">
                <Users className="h-4 w-4 flex-shrink-0" />
                <span>Autre partie: {consultation.other_party_name}</span>
              </div>
            )}
          </div>

          {/* Payment status */}
          {showPaymentStatus && (
            <div className="pt-2 border-t border-border">
              <PaymentProgress
                seekerPaid={consultation.seeker_paid}
                otherPartyPaid={consultation.other_party_paid}
                seekerName={consultation.seeker?.full_name || 'Demandeur'}
                otherPartyName={consultation.other_party_name || 'Autre partie'}
                amountPaidCents={consultation.amount_paid_cents}
                durationMin={consultation.duration_min}
                meetingNumber={consultation.meeting_number}
                variant="compact"
              />
            </div>
          )}

          {/* Receipt download — available once both parties have paid */}
          {consultation.seeker_paid && consultation.other_party_paid && (
            <DownloadReceiptButton
              consultationId={consultation.id}
              variant="outline"
              size="sm"
            />
          )}

          {/* Action buttons */}
          <div className="flex gap-2 pt-2">
            <Dialog>
              <DialogTrigger asChild>
                <Button variant="outline" size="sm" className="flex-1 relative">
                  <MessageSquare className="h-4 w-4 mr-2" />
                  Messages
                  {unreadCount > 0 && (
                    <UnreadBadge count={unreadCount} className="absolute -top-2 -right-2" />
                  )}
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg max-h-[80vh] flex flex-col">
                <DialogHeader>
                  <DialogTitle>Messages - {otherPartyName}</DialogTitle>
                </DialogHeader>
                <div className="flex-1 overflow-hidden">
                  <MessageThread
                    consultationId={consultation.id}
                    userType={userType}
                    otherPartyName={otherPartyName}
                  />
                </div>
              </DialogContent>
            </Dialog>
            
            {onDetailsClick && (
              <Button variant="default" size="sm" className="flex-1" onClick={onDetailsClick}>
                Détails
              </Button>
            )}

            {onScheduleFollowUp && userType === 'mediator' && 
              ['paid', 'payment_completed', 'confirmed', 'completed'].includes(consultation.status || '') && (
              <Button variant="outline" size="sm" onClick={onScheduleFollowUp} title="Planifier une séance suivante">
                <RefreshCw className="h-4 w-4 mr-1" />
                Séance suivante
              </Button>
            )}

            {canReschedule && (
              <AlertDialog open={rescheduleOpen} onOpenChange={setRescheduleOpen}>
                <AlertDialogTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    title="Annuler et choisir un nouveau créneau"
                    disabled={cancelMutation.isPending}
                  >
                    <CalendarClock className="h-4 w-4 mr-1" />
                    Reprogrammer
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Reprogrammer cette consultation ?</AlertDialogTitle>
                    <AlertDialogDescription>
                      La consultation actuelle sera annulée (créneaux libérés et
                      remboursement Stripe automatique des paiements déjà effectués),
                      puis vous serez redirigé(e) vers la recherche pour choisir une
                      nouvelle date avec ce médiateur ou un autre.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <div className="space-y-2">
                    <Label htmlFor={`reschedule-reason-${consultation.id}`}>
                      Raison (optionnel)
                    </Label>
                    <Textarea
                      id={`reschedule-reason-${consultation.id}`}
                      placeholder="Ex. : empêchement professionnel, problème de transport…"
                      value={cancelReason}
                      onChange={(e) => setCancelReason(e.target.value)}
                      rows={3}
                    />
                  </div>
                  <AlertDialogFooter>
                    <AlertDialogCancel disabled={cancelMutation.isPending}>
                      Revenir
                    </AlertDialogCancel>
                    <AlertDialogAction
                      onClick={(e) => {
                        e.preventDefault();
                        handleConfirmReschedule();
                      }}
                      disabled={cancelMutation.isPending}
                    >
                      {cancelMutation.isPending ? (
                        <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                      ) : null}
                      Confirmer et choisir un nouveau créneau
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}

            {isCancellable && (
              <AlertDialog open={cancelOpen} onOpenChange={setCancelOpen}>
                <AlertDialogTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-destructive hover:text-destructive hover:bg-destructive/10"
                    title="Annuler la consultation"
                    disabled={cancelMutation.isPending}
                  >
                    {cancelMutation.isPending ? (
                      <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                    ) : (
                      <XCircle className="h-4 w-4 mr-1" />
                    )}
                    Annuler
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Annuler cette consultation ?</AlertDialogTitle>
                    <AlertDialogDescription>
                      Cette action est définitive. Les créneaux réservés seront immédiatement
                      libérés et l'autre partie sera informée du changement de statut.
                      {(consultation.seeker_paid || consultation.other_party_paid) && (
                        <span className="block mt-2 font-medium text-foreground">
                          Un ou plusieurs paiements ont déjà été effectués&nbsp;:
                          un remboursement Stripe sera lancé automatiquement.
                        </span>
                      )}
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <div className="space-y-2">
                    <Label htmlFor={`cancel-reason-${consultation.id}`}>
                      Raison (optionnel)
                    </Label>
                    <Textarea
                      id={`cancel-reason-${consultation.id}`}
                      placeholder="Ex. : changement de programme, conflit résolu…"
                      value={cancelReason}
                      onChange={(e) => setCancelReason(e.target.value)}
                      rows={3}
                    />
                  </div>
                  <AlertDialogFooter>
                    <AlertDialogCancel disabled={cancelMutation.isPending}>
                      Revenir
                    </AlertDialogCancel>
                    <AlertDialogAction
                      onClick={(e) => {
                        e.preventDefault();
                        runCancel();
                      }}
                      disabled={cancelMutation.isPending}
                      className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                    >
                      {cancelMutation.isPending ? (
                        <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                      ) : null}
                      Confirmer l'annulation
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}

            {within24h && !isTerminal && (
              <Badge variant="outline" className="text-xs whitespace-normal">
                Annulation impossible &lt; 24 h
              </Badge>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
